/**
 * Customer detail: communication history (tracker items 35, 59) - the customer's
 * messages from the outbox (newest first, status filter, load more, link to the
 * job / invoice / estimate / payment each came from, expandable text), the notifications opt-out toggle, and warnings
 * when the customer has no usable email address. ADMIN and OFFICE_MANAGER.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ICustomersApi, INotificationsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { OutboxStatusBadge } from './NotificationBadges';
import { OUTBOX_STATUS_LABELS, eventLabel, messageSourceLink, messageStatusText } from '../pages/settings/notificationUi';
import { inputClass, rowActionClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';

interface Props {
  customer: types.Customer;
  canEdit: boolean;
  onCustomerChange: (customer: types.Customer) => void;
}

const PAGE_SIZE = 20;

export function CustomerCommunicationPanel({ customer, canEdit, onCustomerChange }: Props) {
  const api: INotificationsApi & ICustomersApi = useMemo(() => createAPIClient(), []);
  const [messages, setMessages] = useState<types.OutboxMessage[] | null>(null);
  const [status, setStatus] = useState<types.OutboxStatus | ''>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Page 1 replaces the list (first load, filter change); later pages append
  const load = useCallback(async (targetPage: number) => {
    try {
      const result = await api.listOutbox({ customerId: customer.id, status }, targetPage, PAGE_SIZE);
      setMessages((prev) => (targetPage === 1 || !prev ? result.data : [...prev, ...result.data]));
      setHasMore(result.pagination.page < result.pagination.pages);
      setPage(targetPage);
      setError(null);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load communication history'));
    }
  }, [api, customer.id, status]);

  useEffect(() => { setMessages(null); void load(1); }, [load]);

  async function loadMore() {
    setLoadingMore(true);
    await load(page + 1);
    setLoadingMore(false);
  }

  async function toggleDunningExempt(value: boolean) {
    setBusy(true);
    setError(null);
    try {
      onCustomerChange(await api.updateCustomer(customer.id, { dunningExempt: value }));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not update the reminder exclusion'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleOptOut(value: boolean) {
    setBusy(true);
    setError(null);
    try {
      onCustomerChange(await api.updateCustomer(customer.id, { notificationOptOut: value }));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not update the opt-out'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" aria-labelledby="communication-heading">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 id="communication-heading" className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Communication history</h2>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-gray-700">
            <input type="checkbox" checked={!!customer.notificationOptOut} disabled={!canEdit || busy}
              onChange={(e) => void toggleOptOut(e.target.checked)} />
            Opted out of reminders
          </label>
          <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-gray-700" title="Automated dunning never sends this customer overdue reminders">
            <input type="checkbox" checked={!!customer.dunningExempt} disabled={!canEdit || busy}
              onChange={(e) => void toggleDunningExempt(e.target.checked)} />
            Exclude from overdue reminders
          </label>
        </div>
      </div>
      {!customer.email && (
        <p role="note" className="mb-3 rounded bg-yellow-50 p-3 text-sm text-yellow-900">
          This customer has no email address, so invoices, statements and receipts cannot be emailed.
        </p>
      )}
      {customer.email && customer.emailInvalid && (
        <p role="note" className="mb-3 rounded bg-red-50 p-3 text-sm text-red-800 break-words">
          Email to {customer.email} bounced or was reported as spam. Nothing more is sent to it until the address is changed.
        </p>
      )}
      <div className="mb-3">
        <label htmlFor="communication-status" className="sr-only">Filter by status</label>
        <select id="communication-status" className={`${inputClass} sm:w-auto`} value={status}
          onChange={(e) => setStatus(e.target.value as types.OutboxStatus | '')}>
          <option value="">All messages</option>
          {types.OUTBOX_STATUSES.map((s) => <option key={s} value={s}>{OUTBOX_STATUS_LABELS[s]}</option>)}
        </select>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {messages === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-gray-500">{status ? 'No messages with this status.' : 'No messages yet.'}</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {messages.map((m) => {
            const source = messageSourceLink(m);
            return (
              <li key={m.id} className="py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 break-words">
                    <button type="button" className={`${rowActionClass} text-left`} aria-expanded={open === m.id}
                      onClick={() => setOpen(open === m.id ? null : m.id)}>
                      {eventLabel(m.eventKey)}
                    </button>
                    {m.subject && <span className="text-gray-600"> · {m.subject}</span>}
                    <span className="block text-xs text-gray-500">
                      {new Date(m.sentAt ?? m.createdAt).toLocaleString()}
                      {` · ${m.channel === 'SMS' ? 'Text' : 'Email'}`}{m.toAddress ? ` to ${m.toAddress}` : ''}
                      {source && <> · <Link className="text-blue-700 hover:underline" to={source.to}>{source.label}</Link></>}
                    </span>
                  </div>
                  <div className="text-right">
                    <OutboxStatusBadge status={m.status} />
                    {(m.status === 'SKIPPED' || m.status === 'FAILED' || m.deliveryStatus) && (
                      <span className="block text-xs text-gray-600">{messageStatusText(m)}</span>
                    )}
                  </div>
                </div>
                {open === m.id && (
                  <pre className="mt-2 rounded bg-gray-50 p-3 whitespace-pre-wrap break-words font-sans text-gray-800">{m.body}</pre>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {hasMore && (
        <button type="button" className={`${secondaryButtonClass} mt-3`} disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );
}
